import {
  AcceptInvitePreview,
  type AcceptInviteRequest,
  type LoginRequest,
  LoginResponse,
  MeResponse,
  type PasswordChangeRequest,
  type PasswordResetConfirmRequest,
  type PasswordResetRequest,
} from '@staiter/shared';
import { z } from 'zod';
import { getActiveEntityHeaders, useActiveEntityStore } from '~/store/active-entity';
import { useAuthStore } from '~/store/auth';
// TODO(S1): заменить stub schemas на shared api-contracts.
import {
  ApiRequestError,
  apiRequest,
  redirectToLogin,
  refreshSession,
  tokenStorage,
} from './client';
import { isEntityForbidden } from './entity-forbidden';

// SET-AUDIT-4: login может вернуть либо tokens (LoginResponse), либо MFA challenge.
const MfaChallengeResponse = z.object({
  requires_2fa: z.literal(true),
  challenge_token: z.string(),
  mfa_method: z.enum(['totp']),
});

const LoginOr2faResponse = z.union([LoginResponse, MfaChallengeResponse]);

export type LoginOr2faResult =
  | { kind: 'tokens'; data: z.infer<typeof LoginResponse> }
  | { kind: '2fa'; challenge_token: string };

export async function login(payload: LoginRequest): Promise<LoginOr2faResult> {
  const raw = await apiRequest('/auth/login', LoginOr2faResponse, {
    method: 'POST',
    body: payload,
    skipAuth: true,
  });
  if ('requires_2fa' in raw && raw.requires_2fa) {
    return { kind: '2fa', challenge_token: raw.challenge_token };
  }
  return { kind: 'tokens', data: raw as z.infer<typeof LoginResponse> };
}

export async function verifyLogin2fa(payload: {
  challenge_token: string;
  code?: string;
  recovery_code?: string;
}): Promise<LoginResponse> {
  return apiRequest('/auth/login/2fa', LoginResponse, {
    method: 'POST',
    body: payload,
    skipAuth: true,
  });
}

/**
 * ACC-B1 (пак `tails-closeout`, С-I, F108) — профиль по АКТИВНОЙ компании.
 *
 * `/auth/me` в списке путей без автоматического `X-Active-Entity` (устаревший id из
 * localStorage не должен ронять загрузку 403), и сервер считал роль, права и модули по
 * `users.default_entity_id`, которую соседний запрос переписывает не чаще раза в минуту.
 * Перечитанный после смены компании профиль (ACC-F1) приходил с модулями прежней: «Перейти
 * в „ТОО Альфа“» на экране отказа оставлял отказ. Когда компания уже выбрана (стор
 * проверил её по доступным), заголовок ставится явно; до гидрации — нет.
 *
 * С-I2 Р.3 — ОДНО ПРАВИЛО с остальными запросами: после гидрации `/me` получает те же
 * заголовки компании, что любой запрос (`getActiveEntityHeaders`). В «Все компании» и в
 * холдинге это `X-Active-Scope`, без `X-Active-Entity`, — и сервер (`requireAuth`) считает
 * роль по компании по умолчанию, как для соседних запросов; раньше `/me` судил по выбранной
 * компании, и меню расходилось с тем, что сервер разрешал. Доступ к выбранной компании отозвали
 * посреди сессии (403 `ENTITY_FORBIDDEN`) — выбор сбрасывается и профиль читается без
 * заголовка, а не выход на логин.
 *
 * С-I3 Р.1 (ACC-B3) — профиль без заголовка — это снова компания ТОКЕНА, и если отключено членство
 * именно в ней (у большинства сотрудников компания одна), повтор падал тем же 403. Теперь сначала
 * обновление токена — сервер выдаёт его на активную компанию, — затем повтор без заголовка;
 * активных компаний нет (обновление отказало) — выход. Аккаунт отключён — 401: выход делает
 * `apiRequest`.
 */
export async function fetchMe(): Promise<MeResponse> {
  const store = useActiveEntityStore.getState();
  const tenancy = store.isInitialized ? getActiveEntityHeaders() : {};
  try {
    return await apiRequest(
      '/auth/me',
      MeResponse,
      Object.keys(tenancy).length > 0 ? { headers: tenancy } : {},
    );
  } catch (err) {
    if (isEntityForbidden(err)) {
      if (!(await refreshSession())) {
        // Круг 1 гейта Р.1 (UI и UX, MAJOR): вход называет компанию, к которой отключили доступ.
        redirectToLogin({
          reason: 'access_revoked',
          entity_name: useAuthStore.getState().user?.active_entity_name ?? null,
        });
        throw err;
      }
      const me = await apiRequest('/auth/me', MeResponse, {});
      if (tenancy['X-Active-Entity']) {
        useActiveEntityStore.getState().revokeActiveEntity(me as never);
      }
      return me;
    }
    throw err;
  }
}

export async function logout(): Promise<void> {
  await apiRequest('/auth/logout', z.unknown(), { method: 'POST' });
}

export async function changePassword(payload: PasswordChangeRequest): Promise<void> {
  await apiRequest('/auth/password-change', z.unknown(), {
    method: 'POST',
    body: {
      current_password: payload.current_password,
      new_password: payload.new_password,
    },
  });
}

/**
 * SET-AUDIT-9 (pass3 settings audit): self-edit full_name + phone.
 */
const ProfileResponse = z.object({
  full_name: z.string().nullable(),
  phone: z.string().nullable(),
});

export async function updateProfile(payload: { full_name?: string | null; phone?: string | null }) {
  return apiRequest('/auth/me/profile', ProfileResponse, {
    method: 'PATCH',
    body: payload,
  });
}

/**
 * Профиль — загрузка аватара (multipart). apiRequest JSON-only, поэтому raw fetch
 * с Bearer + FormData. Возвращает presigned avatar_url.
 */
const AvatarResponse = z.object({ avatar_url: z.string().nullable() });

export async function uploadAvatar(file: File): Promise<{ avatar_url: string | null }> {
  const form = new FormData();
  form.append('file', file);
  const token = tokenStorage.getAccess();
  const res = await fetch('/api/v1/auth/me/avatar', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { code?: string; message?: string };
    throw new ApiRequestError(res.status, { code: body.code ?? 'server.internal' });
  }
  return AvatarResponse.parse(await res.json());
}

export async function deleteAvatar(): Promise<{ avatar_url: string | null }> {
  return apiRequest('/auth/me/avatar', AvatarResponse, { method: 'DELETE' });
}

/** Профиль — запрос смены email (письмо-подтверждение на новый адрес). */
export async function requestEmailChange(payload: {
  new_email: string;
  current_password: string;
}): Promise<void> {
  await apiRequest('/auth/me/email/request-change', z.unknown(), {
    method: 'POST',
    body: payload,
  });
}

/** Публичное подтверждение смены email по токену из письма. */
export async function confirmEmailChange(token: string): Promise<void> {
  await apiRequest('/auth/email-change/confirm', z.unknown(), {
    method: 'POST',
    body: { token },
    skipAuth: true,
  });
}

export async function getInvitePreview(token: string): Promise<AcceptInvitePreview> {
  return apiRequest(
    `/auth/invite/preview?token=${encodeURIComponent(token)}`,
    AcceptInvitePreview,
    {
      skipAuth: true,
    },
  );
}

export async function acceptInvite(payload: AcceptInviteRequest): Promise<LoginResponse> {
  return apiRequest('/auth/accept-invite', LoginResponse, {
    method: 'POST',
    body: {
      token: payload.token,
      new_password: payload.new_password,
    },
    skipAuth: true,
  });
}

export async function requestPasswordReset(payload: PasswordResetRequest): Promise<void> {
  await apiRequest('/auth/password-reset/request', z.unknown(), {
    method: 'POST',
    body: payload,
    skipAuth: true,
  });
}

export async function confirmPasswordReset(payload: PasswordResetConfirmRequest): Promise<void> {
  await apiRequest('/auth/password-reset/confirm', z.unknown(), {
    method: 'POST',
    body: {
      token: payload.token,
      new_password: payload.new_password,
    },
    skipAuth: true,
  });
}

// SET-AUDIT-4: 2FA TOTP API.
const TotpStatusSchema = z.object({
  enabled: z.boolean(),
  configured: z.boolean(),
  recovery_codes_remaining: z.number().int(),
  last_used_at: z.string().nullable().optional(),
});

const TotpSetupSchema = z.object({
  secret: z.string(),
  otpauth_uri: z.string(),
  issuer: z.string(),
  account: z.string(),
});

const TotpVerifySchema = z.object({
  ok: z.boolean(),
  just_enabled: z.boolean(),
  recovery_codes: z.array(z.string()).nullable(),
});

const RecoveryCodesSchema = z.object({
  recovery_codes: z.array(z.string()),
});

export async function fetchTotpStatus() {
  return apiRequest('/me/2fa/status', TotpStatusSchema);
}

export async function setupTotp() {
  return apiRequest('/me/2fa/setup', TotpSetupSchema, { method: 'POST', body: {} });
}

export async function verifyTotp(code: string) {
  return apiRequest('/me/2fa/verify', TotpVerifySchema, {
    method: 'POST',
    body: { code },
  });
}

export async function disableTotp(payload: {
  password: string;
  code?: string;
  recovery_code?: string;
}) {
  return apiRequest('/me/2fa/disable', z.object({ ok: z.boolean() }), {
    method: 'POST',
    body: payload,
  });
}

export async function regenerateRecoveryCodes(code: string) {
  return apiRequest('/me/2fa/recovery-codes/regenerate', RecoveryCodesSchema, {
    method: 'POST',
    body: { code },
  });
}
